import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './Tooltip.css';

const Tooltip = ({ 
    children, 
    content,
    wrapperStyle = {},
    tooltipStyle = {}, 
    size = 'nowrap',
    arrow = true,
    placement = 'right',
    trigger = 'hover',
    className = ''
  }) => {

  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0, height: 0 });
  const backgroundColor = tooltipStyle.background || tooltipStyle.backgroundColor || '#1B1925';
  const textColor = tooltipStyle.color || 'white';
  const tooltipWrapperRef = useRef(null);
  const triggerRef = useRef(null);

  const getSizeClasses = () => {
    switch (size) {
      case 'sm': return 'tooltip-sm';
      case 'md': return 'tooltip-md';
      case 'lg': return 'tooltip-lg';
      case 'nowrap': return 'tooltip-no-wrap';
      default: return 'tooltip-no-wrap';
    }
  };

  const getPlacementClasses = () => {
    switch (placement) {
      case 'top': return 'tooltip-top';
      case 'bottom': return 'tooltip-bottom';
      case 'left': return 'tooltip-left';
      case 'right': return 'tooltip-right';
      case 'top-start': return 'tooltip-top-start';
      case 'top-end': return 'tooltip-top-end';
      case 'bottom-start': return 'tooltip-bottom-start';
      case 'bottom-end': return 'tooltip-bottom-end';
      case 'left-start': return 'tooltip-left-start';
      case 'left-end': return 'tooltip-left-end';
      case 'right-start': return 'tooltip-right-start';
      case 'right-end': return 'tooltip-right-end';
      default: return 'tooltip-right';
    }
  };

  const updatePosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        height: rect.height
      });
    }
  };

  const showTooltip = () => {
    updatePosition();
    setVisible(true);
  };

  const hideTooltip = () => {
    setVisible(false);
  };

  let triggerProps = {};

  switch(trigger) {
    case "hover":
      triggerProps = {
        onMouseEnter: showTooltip,
        onMouseLeave: hideTooltip
      };
      break;
    case "click":
      triggerProps = {
        onClick: () => visible ? hideTooltip() : showTooltip()
      };
      break;
    default:
      triggerProps = {
        onMouseEnter: showTooltip,
        onMouseLeave: hideTooltip
      };
  }

  useEffect(() => {
    if (trigger !== 'click' || !visible) return;

    const handleClickOutside = (e) => {
      if (tooltipWrapperRef.current && !tooltipWrapperRef.current.contains(e.target) &&
          triggerRef.current && !triggerRef.current.contains(e.target)) {
        setVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [trigger, visible]);

  useEffect(() => {
    if (!visible) return;

    const handleUpdate = () => updatePosition();
    
    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);
    
    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [visible]);

  const getTooltipPositionStyle = () => {
    const baseStyle = {
      position: 'absolute',
      backgroundColor: backgroundColor,
      color: textColor,
      opacity: 1,
      '--tooltip-bg': backgroundColor,
      ...tooltipStyle,
    };

    // Calculate positions based on placement
    const { top, left, width, height } = position;

    switch (placement) {
      case 'top':
        return {
          ...baseStyle,
          bottom: `calc(100vh - ${top}px + 10px)`,
          left: `${left + width / 2}px`,
          transform: 'translateX(-50%)',
        };
      case 'top-start':
        return {
          ...baseStyle,
          bottom: `calc(100vh - ${top}px + 10px)`,
          left: `${left}px`,
        };
      case 'top-end':
        return {
          ...baseStyle,
          bottom: `calc(100vh - ${top}px + 10px)`,
          left: `${left + width}px`,
          transform: 'translateX(-100%)',
        };
      case 'bottom':
        return {
          ...baseStyle,
          top: `${top + height + 10}px`,
          left: `${left + width / 2}px`,
          transform: 'translateX(-50%)',
        };
      case 'bottom-start':
        return {
          ...baseStyle,
          top: `${top + height + 10}px`,
          left: `${left}px`,
        };
      case 'bottom-end':
        return {
          ...baseStyle,
          top: `${top + height + 10}px`,
          left: `${left + width}px`,
          transform: 'translateX(-100%)',
        };
      case 'left':
        return {
          ...baseStyle,
          top: `${top + height / 2}px`,
          left: `${left - 10}px`,
          transform: 'translate(-100%, -50%)',
        };
      case 'left-start':
        return {
          ...baseStyle,
          top: `${top}px`,
          left: `${left - 10}px`,
          transform: 'translateX(-100%)',
        };
      case 'left-end':
        return {
          ...baseStyle,
          top: `${top + height}px`,
          left: `${left - 10}px`,
          transform: 'translate(-100%, -100%)',
        };
      case 'right':
        return {
          ...baseStyle,
          top: `${top + height / 2}px`,
          left: `${left + width + 10}px`,
          transform: 'translateY(-50%)',
        };
      case 'right-start':
        return {
          ...baseStyle,
          top: `${top}px`,
          left: `${left + width + 10}px`,
        };
      case 'right-end':
        return {
          ...baseStyle,
          top: `${top + height}px`,
          left: `${left + width + 10}px`,
          transform: 'translateY(-100%)',
        };
      default:
        return {
          ...baseStyle,
          top: `${top + height / 2}px`,
          left: `${left + width + 10}px`,
          transform: 'translateY(-50%)',
        };
    }
  };

  const tooltipContent = visible && createPortal(
    <div 
      ref={tooltipWrapperRef}
      style={getTooltipPositionStyle()}
      className={`tooltip-container tooltip-portal ${className} ${getSizeClasses()} ${getPlacementClasses()} ${arrow ? 'with-arrow' : ''}`}
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={trigger === 'hover' ? () => setVisible(true) : undefined}
      onMouseLeave={trigger === 'hover' ? () => setVisible(false) : undefined}
    >
      {content}
    </div>,
    document.body
  );

  return (
    <>
      <div 
        ref={triggerRef}
        style={{...wrapperStyle, display: 'inline-block' }}
        {...triggerProps}
      >
        {children}
      </div>
      {tooltipContent}
    </>
  );
};

export default Tooltip;